import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePublications } from '../contexts/PublicationsContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import useSEO from '../components/SEO';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Download } from 'lucide-react';



const PublicationDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { getPublicationById, isLoading } = usePublications();

    const publication = id ? getPublicationById(id) : undefined;

    useSEO({
        title: publication ? `${publication.title} - ISTMECH` : 'Publication - ISTMECH',
        description: publication ? publication.excerpt : "Détail d'une publication de l'ISTMECH.",
        keywords: 'ISTMECH, publication, actualités, annonces, événements',
        url: publication ? `https://www.istmech.edu.ht/publication/${publication.id}` : 'https://www.istmech.edu.ht/publication',
        image: publication?.imageUrl || 'https://www.istmech.edu.ht/og-image.png'
    });

    const handlePrintTable = () => {
        const tableElement = document.querySelector('.prose table');
        if (!tableElement) return;

        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charSet="utf-8" />
  <title>${publication?.title || 'Tableau'} - ISTMECH</title>
  <style>
    body { font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 24px; }
    h1 { font-size: 20px; margin-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; font-size: 11px; }
    th, td { border: 1px solid #000; padding: 4px 6px; }
    th { text-align: center; font-weight: 600; background-color: #1e293b; color: white; }
    td { text-align: center; }
  </style>
</head>
<body>
  <h1>${publication?.title || 'Tableau'}</h1>
  ${tableElement.outerHTML}
</body>
</html>`;

        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };

    const formatDate = (date: Date) => {
        return new Intl.DateTimeFormat('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        }).format(date);
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-gray-700 dark:text-gray-300">Chargement de la publication...</div>
            </div>
        );
    }

    if (!publication) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <Header />
                <main className="pt-24 pb-16 max-w-4xl mx-auto px-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 text-center">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Publication introuvable</h1>
                        <p className="text-gray-600 dark:text-gray-300 mb-6">
                            La publication que vous recherchez n'existe pas ou n'est plus disponible.
                        </p>
                        <button
                            onClick={() => navigate('/#publications')}
                            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200"
                        >
                            Retour aux publications
                        </button>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    const getCategoryColor = (category: string) => {
        switch (category) {
            case 'actualite':
                return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200';
            case 'annonce':
                return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200';
            case 'evenement':
                return 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200';
            case 'formation':
                return 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-200';
            default:
                return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
        }
    };

    const getCategoryIcon = (category: string) => {
        switch (category) {
            case 'actualite':
                return '📢';
            case 'annonce':
                return '📋';
            case 'evenement':
                return '🎉';
            case 'formation':
                return '🎓';
            default:
                return '📰';
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
            <Header />
            <main className="pt-24 pb-16 max-w-4xl mx-auto px-4">
                <button
                    onClick={() => navigate('/#publications')}
                    className="mb-6 inline-flex items-center text-sm text-blue-600.dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Retour aux publications
                </button>

                <article className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
                    {publication.imageUrl && (
                        <img
                            src={publication.imageUrl}
                            alt={publication.title}
                            className="w-full h-64 object-cover"
                        />
                    )}

                    <div className="p-6 sm:p-8 space-y-4">
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                            <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(
                                    publication.category
                                )}`}
                            >
                                {getCategoryIcon(publication.category)} {publication.category}
                            </span>
                            <time className="text-sm text-gray-500 dark:text-gray-400">
                                {formatDate(publication.createdAt)}
                            </time>
                        </div>

                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                            {publication.title}
                        </h1>

                        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                            <span className="font-medium">{publication.author}</span>
                        </div>

                        {publication.tags && publication.tags.length > 0 && (
                            <div className="pt-2 flex flex-wrap gap-2">
                                {publication.tags.map((tag, index) => (
                                    <span
                                        key={index}
                                        className="inline-block px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-md"
                                    >
                                        #{tag}
                                    </span>
                                ))}
                            </div>
                        )}
                        {publication.content.includes('|') && publication.content.includes('---') && (
                            <div className="mt-6 flex justify-end">
                                <button
                                    type="button"
                                    onClick={handlePrintTable}
                                    className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-colors duration-200"
                                >

                                    <Download className="w-4 h-4 mr-2" />
                                    Télécharger le tableau en PDF
                                </button>
                            </div>
                        )}
                        <div className="mt-6 prose dark:prose-invert max-w-none leading-relaxed">
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    table: ({ node, ...props }) => (
                                        <div
                                            className="my-4 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700"
                                            style={{
                                                scrollbarWidth: 'thin',
                                                scrollbarColor: '#9ca3af #f3f4f6'
                                            }}
                                        >
                                            <table className="min-w-full text-sm border-collapse" {...props} />
                                        </div>
                                    ),
                                    thead: ({ node, ...props }) => (
                                        <thead className="bg-[#1e293b] text-white [&_tr]:border-b" {...props} />
                                    ),
                                    tbody: ({ node, ...props }) => (
                                        <tbody className="[&_tr:last-child]:border-0" {...props} />
                                    ),
                                    tr: ({ node, ...props }) => (
                                        <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted even:bg-muted/20" {...props} />
                                    ),
                                    th: ({ node, ...props }) => (
                                        <th className="h-12 px-4 text-left align-middle font-medium text-white/90 border border-slate-700 whitespace-nowrap" {...props} />
                                    ),
                                    td: ({ node, ...props }) => (
                                        <td className="p-4 align-middle border border-gray-300 dark:border-gray-700 whitespace-nowrap" {...props} />
                                    ),
                                }}
                            >
                                {publication.content}
                            </ReactMarkdown>
                        </div>


                    </div>
                </article>
            </main>
            <Footer />
        </div >
    );
};

export default PublicationDetail;
